import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { TasksIcon } from './product-icons'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/* Tasks deep-dive, task-list rebuild plus three supporting cards.
 * Copy is locale-keyed — see the note in `crm-section.tsx`. */

type Priority = { label: string; className: string }
type TaskRow = { done: boolean; name: string; priority: Priority; who: string }
type Tag = { color: string; label: string }

type TasksContent = {
  heading: string
  tagline: string
  leadHeading: string
  leadBody: readonly [string, string]
  sprintLabel: string
  filterLabel: string
  sortLabel: string
  columns: { task: string; owner: string; priority: string }
  rows: readonly TaskRow[]
  filtersCard: { title: string; body: string; chips: readonly string[] }
  labelsCard: { title: string; body: string; tags: readonly Tag[] }
  teamCard: { title: string; body: string; names: readonly [string, string] }
}

const PRIORITY = {
  en: {
    high: { className: 'text-[#c4502f]', label: 'High' },
    low: { className: 'text-[#7a7a80]', label: 'Low' },
    medium: { className: 'text-[#b07d24]', label: 'Medium' },
  },
  'pt-BR': {
    high: { className: 'text-[#c4502f]', label: 'Alta' },
    low: { className: 'text-[#7a7a80]', label: 'Baixa' },
    medium: { className: 'text-[#b07d24]', label: 'Média' },
  },
} as const

const CONTENT: Record<MarketingLocale, TasksContent> = {
  en: {
    columns: { owner: 'Owner', priority: 'Priority', task: 'Task' },
    filterLabel: 'Filter: Status',
    filtersCard: {
      body: 'Build the view that matches your week, or use the default that ships ready.',
      chips: ['Priority: High', 'Owner: me'],
      title: 'Filters and sorting',
    },
    heading: 'Tasks',
    labelsCard: {
      body: 'Tag tasks by discipline, phase, or client and filter the whole board in one click.',
      tags: [
        { color: '#7bb86b', label: 'Plumbing' },
        { color: '#e8a13d', label: 'Approval' },
        { color: '#5b8def', label: 'Client' },
        { color: '#d96fa3', label: 'Urgent' },
        { color: '#9a9aa0', label: 'Handover' },
      ],
      title: 'Labels',
    },
    leadBody: [
      'Enter to create a task, assign an owner and a priority without leaving the keyboard, paste a whole list to get started.',
      'What is for later goes to the backlog, without inflating this week’s sprint. When you would rather see the board, switch to the kanban view and drag between columns.',
    ],
    leadHeading: 'Capture a task in seconds and get back to the drawing',
    rows: [
      { done: true, name: 'Review the section drawings', priority: PRIORITY.en.high, who: 'Maya' },
      { done: true, name: 'Update the outline spec', priority: PRIORITY.en.medium, who: 'Maya' },
      {
        done: false,
        name: 'Coordinate the plumbing set',
        priority: PRIORITY.en.medium,
        who: 'Maya',
      },
      { done: false, name: 'File the permit application', priority: PRIORITY.en.low, who: 'Maya' },
      {
        done: false,
        name: 'Bring the survey into the model',
        priority: PRIORITY.en.low,
        who: 'Maya',
      },
      {
        done: true,
        name: 'Close the Foundry Lane proposal',
        priority: PRIORITY.en.high,
        who: 'Ryan',
      },
      {
        done: true,
        name: 'Detail the kitchen millwork',
        priority: PRIORITY.en.medium,
        who: 'Ryan',
      },
      { done: false, name: 'Select the tile package', priority: PRIORITY.en.high, who: 'Ryan' },
      { done: false, name: 'Schedule the site visit', priority: PRIORITY.en.medium, who: 'Chloe' },
    ],
    sortLabel: 'Sort: priority',
    sprintLabel: 'Current sprint',
    tagline: 'A board that shows what each person is delivering this week',
    teamCard: {
      body: 'See in one place what everyone in the practice has to deliver, across every project.',
      names: ['Maya E.', 'Ryan C.'],
      title: 'Team workload',
    },
  },
  'pt-BR': {
    columns: { owner: 'Responsável', priority: 'Prioridade', task: 'Tarefa' },
    filterLabel: 'Filtro: Status',
    filtersCard: {
      body: 'Monte a visão que combina com a sua semana, ou use o padrão que já vem pronto.',
      chips: ['Prioridade: Alta', 'Responsável: eu'],
      title: 'Filtros e ordenação',
    },
    heading: 'Tarefas',
    labelsCard: {
      body: 'Marque tarefas por disciplina, etapa ou cliente e filtre o quadro inteiro em um clique.',
      tags: [
        { color: '#7bb86b', label: 'Hidráulica' },
        { color: '#e8a13d', label: 'Aprovação' },
        { color: '#5b8def', label: 'Cliente' },
        { color: '#d96fa3', label: 'Urgente' },
        { color: '#9a9aa0', label: 'Recebimento' },
      ],
      title: 'Etiquetas',
    },
    leadBody: [
      'Enter para criar uma tarefa, atribua responsável e prioridade sem soltar o teclado, cole uma lista inteira para começar.',
      'O que é para depois vai para o backlog, sem inflar o sprint da semana. Quando preferir o quadro, alterne para a visão kanban e arraste entre as colunas.',
    ],
    leadHeading: 'Anote uma tarefa em segundos e volte para a prancheta',
    rows: [
      {
        done: true,
        name: 'Revisar prancha de cortes',
        priority: PRIORITY['pt-BR'].high,
        who: 'Marina',
      },
      {
        done: true,
        name: 'Atualizar memorial descritivo',
        priority: PRIORITY['pt-BR'].medium,
        who: 'Marina',
      },
      {
        done: false,
        name: 'Compatibilizar projeto hidráulico',
        priority: PRIORITY['pt-BR'].medium,
        who: 'Marina',
      },
      {
        done: false,
        name: 'Protocolar alvará na prefeitura',
        priority: PRIORITY['pt-BR'].low,
        who: 'Marina',
      },
      {
        done: false,
        name: 'Lançar levantamento no modelo',
        priority: PRIORITY['pt-BR'].low,
        who: 'Marina',
      },
      {
        done: true,
        name: 'Fechar proposta, Loja Centro',
        priority: PRIORITY['pt-BR'].high,
        who: 'Rafael',
      },
      {
        done: true,
        name: 'Detalhar marcenaria da cozinha',
        priority: PRIORITY['pt-BR'].medium,
        who: 'Rafael',
      },
      {
        done: false,
        name: 'Selecionar revestimentos',
        priority: PRIORITY['pt-BR'].high,
        who: 'Rafael',
      },
      {
        done: false,
        name: 'Agendar visita técnica',
        priority: PRIORITY['pt-BR'].medium,
        who: 'Camila',
      },
    ],
    sortLabel: 'Ordenar: prioridade',
    sprintLabel: 'Sprint atual',
    tagline: 'Um quadro que mostra o que cada pessoa entrega nesta semana',
    teamCard: {
      body: 'Veja em um lugar só o que cada pessoa do escritório tem para entregar, em todos os projetos.',
      names: ['Marina A.', 'Rafael S.'],
      title: 'Trabalho em equipe',
    },
  },
}

const TONE = ['#e8b4a0', '#a9c4e8', '#bcd6a8']

const TaskListMockup = ({ content }: { content: TasksContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(30,50,30,0.3)]">
    <div className="flex items-center gap-3 border-[#f1f1f1] border-b px-4 py-3">
      <span className="flex items-center gap-1.5 font-semibold text-[#1c1c20] text-[12.5px]">
        <span className="size-[7px] rounded-full bg-[#7bb86b]" /> {content.sprintLabel}
      </span>
      <span className="rounded-md bg-[#f3f3f4] px-2 py-1 text-[#6c6c72] text-[11px]">
        {content.filterLabel}
      </span>
      <span className="ml-auto text-[#9a9aa0] text-[11px]">{content.sortLabel}</span>
    </div>
    <div className="grid grid-cols-[1.6fr_0.5fr_0.5fr] bg-[#fafafa] px-4 py-2 text-[#9a9aa0] text-[10.5px]">
      <span>{content.columns.task}</span>
      <span>{content.columns.owner}</span>
      <span>{content.columns.priority}</span>
    </div>
    {content.rows.map((row, i) => (
      <div
        className="grid grid-cols-[1.6fr_0.5fr_0.5fr] items-center border-[#f4f4f4] border-t px-4 py-[9px] text-[12px]"
        key={row.name}
      >
        <span className="flex items-center gap-2">
          <span className="text-[#bdbdc2] text-[10px]">{i + 1}</span>
          <span
            className="flex size-[15px] items-center justify-center rounded-full text-[9px] text-white"
            style={{
              background: row.done ? '#5b8def' : 'transparent',
              border: row.done ? 'none' : '1.5px solid #d4d4d6',
            }}
          >
            {row.done ? '✓' : ''}
          </span>
          <span className="truncate text-[#43434a]">{row.name}</span>
        </span>
        <span className="flex items-center gap-1.5 text-[#6c6c72]">
          <span
            className="size-[16px] rounded-full"
            style={{ background: TONE[i % TONE.length] }}
          />
          {row.who}
        </span>
        <span className={`text-[11px] ${row.priority.className}`}>▮ {row.priority.label}</span>
      </div>
    ))}
  </div>
)

const SmallCard = ({
  visual,
  title,
  body,
}: {
  visual: React.ReactNode
  title: string
  body: string
}) => (
  <div className="rounded-2xl border border-[#e2ebe2] bg-white/70 p-5">
    <div className="flex h-[110px] items-center justify-center overflow-hidden rounded-xl bg-[#eef4ee]">
      {visual}
    </div>
    <h4 className="mt-4 font-bold text-[#1c1c20] text-[16px]">{title}</h4>
    <p className="mt-1.5 text-[#8a8a90] text-[14px] leading-relaxed">{body}</p>
  </div>
)

const Pill = ({ children, tone }: { children: React.ReactNode; tone: string }) => (
  <span
    className="rounded-md px-2 py-1 font-medium text-[11px]"
    style={{ background: tone, color: '#43434a' }}
  >
    {children}
  </span>
)

export const TasksSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section className="relative overflow-hidden bg-[#f3f8f3] py-28">
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="tasks"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <div className="flex items-center justify-center gap-3">
            <TasksIcon className="size-[56px]" />
            <h2 className="font-display text-[#0b0b0d] text-[clamp(48px,8vw,76px)] tracking-[-0.01em]">
              {content.heading}
            </h2>
          </div>
          <p className="mt-3 text-[#8a8a90] text-[clamp(18px,2.6vw,24px)]">{content.tagline}</p>
        </ScrollReveal>

        <div className="mt-16 grid items-center gap-12 md:grid-cols-2 md:gap-10">
          <ScrollReveal>
            <h3 className="font-display text-[#0b0b0d] text-[clamp(28px,4vw,40px)] leading-[1.14] tracking-[-0.01em]">
              {content.leadHeading}
            </h3>
            {content.leadBody.map((paragraph, i) => (
              <p
                className={`text-[#8a8a90] text-[clamp(15px,2vw,19px)] leading-relaxed ${i === 0 ? 'mt-5' : 'mt-4'}`}
                key={paragraph}
              >
                {paragraph}
              </p>
            ))}
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <TaskListMockup content={content} />
          </ScrollReveal>
        </div>

        <div className="mt-20 grid gap-6 md:grid-cols-3">
          <ScrollReveal>
            <SmallCard
              body={content.filtersCard.body}
              title={content.filtersCard.title}
              visual={
                <div className="space-y-2">
                  {content.filtersCard.chips.map((chip) => (
                    <Pill key={chip} tone="#fff">
                      {chip}
                    </Pill>
                  ))}
                </div>
              }
            />
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <SmallCard
              body={content.labelsCard.body}
              title={content.labelsCard.title}
              visual={
                <div className="flex flex-wrap justify-center gap-1.5">
                  {content.labelsCard.tags.map((tag) => (
                    <Pill key={tag.label} tone="#fff">
                      <span
                        className="mr-1 inline-block size-[6px] rounded-full align-middle"
                        style={{ background: tag.color }}
                      />
                      {tag.label}
                    </Pill>
                  ))}
                </div>
              }
            />
          </ScrollReveal>
          <ScrollReveal delay={200}>
            <SmallCard
              body={content.teamCard.body}
              title={content.teamCard.title}
              visual={
                <div className="w-[78%] rounded-lg bg-white p-2 shadow-sm">
                  {content.teamCard.names.map((n, i) => (
                    <div className="flex items-center gap-2 px-1 py-1.5" key={n}>
                      <span className="size-[16px] rounded-full" style={{ background: TONE[i] }} />
                      <span className="text-[#43434a] text-[11px]">{n}</span>
                      {i === 1 && <span className="ml-auto text-[#5b8def] text-[10px]">✓</span>}
                    </div>
                  ))}
                </div>
              }
            />
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
